/**
 * Tracks which preferred-location value last seeded a list's location
 * filter.
 *
 * Persisted filters (usePersistedFilters) freeze the location the user
 * had when the filters were first written. Without a seed marker we
 * can't tell a stale seed apart from a deliberate user selection. The
 * marker records the preference value that produced the current
 * `locations` filter:
 *
 *   - preference changed (profile page) → marker differs → re-seed the
 *     filter with the new preference, update the marker
 *   - user manually changed locations → marker still equals the current
 *     preference → no override, the manual choice survives navigation
 *
 * Session-scoped on purpose: it mirrors the lifetime of the persisted
 * filters themselves. Each consumer passes a unique `namespace`
 * ('tasks' or 'audits') to avoid collisions.
 */

/** Serialized form of "no preference" — distinguishes unset from absent. */
const NONE = '__none__';

function buildSeedKey(namespace: string): string {
  return `gravion:filters:${namespace}:preferred-seed`;
}

/**
 * Read the preference value that last seeded the location filter for
 * the given namespace. Returns `null` when nothing has been recorded.
 */
export function readPreferredLocationSeed(namespace: string): string | undefined | null {
  try {
    const raw = sessionStorage.getItem(buildSeedKey(namespace));
    if (raw === null) return null;
    return raw === NONE ? undefined : raw;
  } catch {
    return null;
  }
}

/** Record the preference value that seeded the location filter. */
export function writePreferredLocationSeed(
  namespace: string,
  value: string | undefined,
): void {
  try {
    sessionStorage.setItem(buildSeedKey(namespace), value ?? NONE);
  } catch {
    // Storage unavailable (private mode, quota). Silently ignore.
  }
}

/**
 * Whether the location filter should be re-seeded from the preferred
 * location. True when the preference differs from what last seeded the
 * filter (including the very first visit, seed === null, which
 * usePersistedFilters already handles via INITIAL_OVERRIDES).
 */
export function shouldReseedLocations(
  preferredLocation: string | undefined,
  seed: string | undefined | null,
): boolean {
  if (seed === null) return false; // first visit — INITIAL_OVERRIDES seeds it
  return preferredLocation !== seed;
}

/**
 * One-shot sync between the preferred location and a persisted
 * `locations` filter. Records the seed on first visit; on a preference
 * change, updates the marker and hands the new filter value to
 * `applyLocations`.
 *
 * @param namespace - unique key per consumer ('tasks' | 'audits')
 */
export function syncPreferredLocationSeed(
  namespace: string,
  preferredLocation: string | undefined,
  applyLocations: (locations: string[]) => void,
): void {
  const seed = readPreferredLocationSeed(namespace);
  if (seed === null) {
    // First visit this session — record what INITIAL_OVERRIDES seeded.
    writePreferredLocationSeed(namespace, preferredLocation);
    return;
  }
  if (shouldReseedLocations(preferredLocation, seed)) {
    writePreferredLocationSeed(namespace, preferredLocation);
    applyLocations(preferredLocation ? [preferredLocation] : []);
  }
}
